import { ApolloError } from "@apollo/client";
import { FieldValues, Path, UseFormSetError } from "react-hook-form";

export const PROFILE_VALIDATION_ERROR_CODE = "VALIDATION_ERROR";
export const PROFILE_DUPLICATE_IDENTITY_ERROR_CODE =
  "PROFILE_DUPLICATE_IDENTITY_NUMBER";

type GraphQLFieldValidationIssue = {
  field: string;
  message: string;
};

type ParsedProfileMutationError = {
  message: string;
  code?: string;
  fieldIssues: GraphQLFieldValidationIssue[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const parseFieldIssues = (
  extensions: Record<string, unknown> | undefined,
): GraphQLFieldValidationIssue[] => {
  if (!extensions) {
    return [];
  }

  const validation = extensions.validation;
  if (!isRecord(validation)) {
    return [];
  }

  const fields = validation.fields;
  if (!Array.isArray(fields)) {
    return [];
  }

  const issues: GraphQLFieldValidationIssue[] = [];

  for (const fieldIssue of fields) {
    if (!isRecord(fieldIssue)) {
      continue;
    }

    const field = fieldIssue.field;
    const message = fieldIssue.message;
    if (typeof field !== "string" || field.trim().length === 0) {
      continue;
    }

    if (typeof message !== "string" || message.trim().length === 0) {
      continue;
    }

    issues.push({ field, message });
  }

  return issues;
};

export const parseProfileMutationError = (
  error: ApolloError,
): ParsedProfileMutationError | null => {
  const graphqlError = error.graphQLErrors[0];
  if (!graphqlError) {
    return null;
  }

  const extensions = isRecord(graphqlError.extensions)
    ? graphqlError.extensions
    : undefined;
  const code = extensions?.code;

  return {
    message: graphqlError.message,
    code: typeof code === "string" ? code : undefined,
    fieldIssues: parseFieldIssues(extensions),
  };
};

export const applyFieldValidationIssues = <TFormValues extends FieldValues>(
  setError: UseFormSetError<TFormValues>,
  validFields: readonly string[],
  issues: GraphQLFieldValidationIssue[],
): string[] => {
  const validFieldsSet = new Set(validFields);
  const unknownFieldMessages: string[] = [];

  for (const issue of issues) {
    if (!validFieldsSet.has(issue.field)) {
      unknownFieldMessages.push(issue.message);
      continue;
    }

    setError(issue.field as Path<TFormValues>, {
      type: PROFILE_VALIDATION_ERROR_CODE,
      message: issue.message,
    });
  }

  return unknownFieldMessages;
};
